import React, {Fragment, useMemo, useRef, useState} from "react";
import * as yup from "yup";
import {Form, Formik} from "formik";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import moment from "moment";
import axios from "axios";
import {Link, Navigate} from "react-router-dom";
import {ServiceType} from "./ServiceType";
import {PickupDetails} from "./PickupDetails";
import {DeliveryDetails} from "./DeliveryDetails";
import {PaymentDrawer} from "./PaymentDrawer";
import {getZone} from "../utils/zone";
import {useNavigate} from "react-router-dom";
import GoBackButton from "./GoBackButton";
import {PhoneNumberUtil} from "google-libphonenumber";

const phoneUtil = PhoneNumberUtil.getInstance();

interface DeliveryDetailsRef {
  scrollToDelivery: (index: number) => void;
}

function calculateTotalBreakdown(values: any) {
  let subtotal = 0;
  values.deliveries.forEach((delivery: any) => {
    subtotal += parseFloat(delivery.amount);
  });
  return subtotal;
}

const validationSchema = yup.object().shape({
  accountType: yup
    .string()
    .oneOf(
      ["WITHIN_LAGOS", "OUTSIDE_LAGOS", "OUTSIDE_NIGERIA"],
      "Please select an account type"
    )
    .required("Account type is required"),
  first_name: yup.string().required("First Name is required"),
  last_name: yup.string().required("Last Name is required"),
  address: yup.string().required("Address is required"),
  city: yup.string().required("Address is required"),
  state: yup.string().required("Address is required"),
  email: yup.string().email(),
  location: yup.string().required("Location is required"),
  type: yup.string().required("Type is required"),
  pickupDate: yup.string().required("Pickup Date is required"),
  pickupTime: yup.string().required("Pickup Time is required"),
  phone_number: yup
    .string()
    .required("Phone Number is required")
    .test("is-valid-phone", "Invalid phone number", function (value) {
      const {path, createError, parent} = this;
      const country = parent.country_code || "NG";

      try {
        if (!value)
          return createError({path, message: "Phone number is required"});

        // Add '+' if missing and likely international
        const formattedValue = value.startsWith("+") ? value : `+${value}`;

        const number = phoneUtil.parse(formattedValue, country);

        if (!phoneUtil.isValidNumber(number)) {
          return createError({path, message: "Invalid phone number"});
        }

        return true;
      } catch (e) {
        return createError({path, message: "Invalid phone number"});
      }
    }),
  orderType: yup
    .string()
    .oneOf(["SINGLE_ORDER", "BATCH_ORDER"], "Please select an order type")
    .required("Order type is required"),

  deliveries: yup
    .array()
    .of(
      yup.object().shape({
        full_name: yup.string().required("Full Name is required"),
        d_address: yup.string().required("Address is required"),
        d_state: yup.string().required("State is required"),
        d_city: yup.string().required("City is required"),
        d_schedule: yup.string().required("Delivery schedule is required"),
        d_phone_number: yup
          .string()
          .required("Phone Number is required")
          .test("is-valid-phone", "Invalid phone number", function (value) {
            const {path, createError, parent} = this;
            const country = parent.country_code || "NG";

            try {
              if (!value)
                return createError({path, message: "Phone number is required"});

              // Add '+' if missing and likely international
              const formattedValue = value.startsWith("+")
                ? value
                : `+${value}`;

              const number = phoneUtil.parse(formattedValue, country);

              if (!phoneUtil.isValidNumber(number)) {
                return createError({path, message: "Invalid phone number"});
              }

              return true;
            } catch (e) {
              return createError({path, message: "Invalid phone number"});
            }
          }),
        d_weight: yup.string().required("Weight is required"),
        //   .test(
        //     "min-max-weight",
        //     "Weight must be between 0.5kg and 15kg",
        //     (value) => {
        //       const numValue = parseFloat(value);
        //       return !isNaN(numValue) && numValue >= 0.5 && numValue <= 15;
        //     }
        //   ),
        d_description: yup.string().required("Description is required"),
      })
    )
    .min(1, "At least one delivery is required"),

  // Other fields (unchanged)
  coupon: yup.string(),
  latitude: yup.number(),
  longitude: yup.number(),
});

export const CreateOrderForm = () => {
  const navigate = useNavigate();

  const Baseurl = process.env.REACT_APP_BASEURL;
  const token = JSON.parse(localStorage.getItem("eftd-admin-token") || "{}")
    ?.data?.data?.token;
  const config = {
    headers: {Authorization: `Bearer ${token}`},
  };
  const now = moment();
  const eightThirty = moment()
    .startOf("day")
    .add(8, "hours")
    .add(30, "minutes");

  let pickupDate: Date;

  if (now.isBefore(eightThirty)) {
    // Before 8:30 AM, pickup is still today
    pickupDate = moment().startOf("day").toDate();
  } else {
    let nextDate = moment().add(1, "day").startOf("day");

    // If next day is Sunday, skip to Monday
    if (nextDate.day() === 0) {
      nextDate = nextDate.add(1, "day");
    }

    // If it's Saturday and past 8:30, pickup should be Monday
    if (now.day() === 6 && now.isAfter(eightThirty)) {
      nextDate = moment().add(2, "days").startOf("day"); // jump to Monday
    }

    pickupDate = nextDate.toDate();
  }
  const initialValues = {
    accountType: null,
    first_name: "",
    last_name: "",
    address: "",
    email: "",
    city: "",
    state: "",
    latitude: "",
    longitude: "",
    phone_number: "",
    location: "",
    type: "individuals",
    pickupType: "HUB_AND_SPOKE",
    pickupDate: pickupDate,
    pickupTime: "7am - 8:30am",
    timezone: "",
    orderType: "SINGLE_ORDER",
    deliveryType: null,
    pickup_address_components: "",

    deliveries: [
      {
        full_name: "",
        d_address: "",
        d_state: "",
        d_state_b: "",
        d_city: "",
        d_schedule: "3 - 5 Business Days",
        d_phone_number: "",
        d_weight: "",
        email: "",
        d_description: "",
        latitude: 0,
        longitude: 0,
        timezone: "",
        amount: "0",
      },
    ],
    coupon: "",
    batchOrderType: "MANUAL_UPLOAD",
  };

  const [isLoading, setIsLoading] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [coupon, setCoupon] = useState(false);
  const [estimate, setEstimate] = useState<any>();
  const deliveryDetailsRef = useRef<DeliveryDetailsRef>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const getEstimate = async (
    values: typeof initialValues,
    setValues: (values: any) => void
  ) => {
    if (!values.state || !values.city) {
      toast.error("Please edit pickup address");
      return;
    }
    if (values.orderType === "BATCH_ORDER" && values.deliveries.length < 2) {
      toast.error("Cannot have 1 order for a multiple order type");
      return;
    }
    setIsLoading(true);

    try {
      //   const weights = values.deliveries.map((delivery) => {
      //     const zone = getZone(
      //       values.accountType!,
      //       values.accountType === "OUTSIDE_NIGERIA"
      //         ? delivery.d_state_b
      //         : delivery.d_state
      //     );

      //     // Check if the zone is valid/covered
      //     if (!zone) {
      //       toast.error(
      //         `Service not available for delivery to ${
      //           values.accountType === "OUTSIDE_NIGERIA"
      //             ? delivery.d_state_b
      //             : delivery.d_state
      //         }. Please check your service type/select a different zone.`
      //       );
      //       return {weight: "0", zone: null};
      //     }

      //     return {
      //       weight: delivery.d_weight.toString(),
      //       zone: zone,
      //     };
      //   });

      //   if (weights.some((w) => w.zone === null)) {
      //     return;
      //   }

      //   const totalWeight = values.deliveries.reduce(
      //     (sum, delivery) => sum + parseFloat(delivery.d_weight),
      //     0
      //   );

      //   if (totalWeight > 45) {
      //     toast.error("Total weight per order should not exceed 45kg");
      //     return;
      //   }

      //   const estimate = await axios.post(
      //     `${Baseurl}/api/admin/orders/estimate`,
      //     {
      //       payloads: weights,
      //       serviceType: values.accountType,
      //       type: values.type,
      //     },
      //     {...config}
      //   );

      //   const {breakdown, totalQuote, hasActiveSubscription, discountPercent} =
      //     estimate.data.data;

      //   setValues({
      //     ...values,
      //     deliveries: values.deliveries.map((delivery, idx) => ({
      //       ...delivery,
      //       amount: breakdown[idx]?.quote ?? "",
      //     })),
      //   });

      //   setEstimate(estimate.data.data);
      setStep(3);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (values: typeof initialValues) => {
    setIsLoading(true);
    const {
      accountType,
      address,
      coupon,
      orderType,
      deliveries,
      batchOrderType,
      deliveryType,
      first_name,
      last_name,
      latitude,
      location,
      longitude,
      phone_number,
      city,
      email,
      pickupDate,
      type,
      pickupTime,
      pickupType,
      timezone,
      state,
    } = values;
    const formData = {
      serviceType: accountType,
      orderType: orderType,
      batchOrderType,
      pickupDetail: {
        firstName: first_name,
        lastName: last_name,
        phoneNumber: phone_number,
        address,
        email: email ? email : undefined,
        latitude: String(latitude),
        longitude: String(longitude),
      },
      deliveryDetails: deliveries.map(
        ({
          d_address,
          d_city,
          d_description,
          d_phone_number,
          d_schedule,
          d_state,
          d_weight,
          email,
          full_name,
          latitude,
          longitude,
          timezone,
          amount,
          d_state_b,
        }) => ({
          deliverySchedule: d_schedule,
          recipientName: full_name,
          state: d_state,
          city: d_city,
          address: d_address,
          country: accountType === "OUTSIDE_NIGERIA" ? d_state_b : undefined,
          phoneNumber: d_phone_number,
          productWeight: `${d_weight}kg`,
          productDescription: d_description,
          deliveryType: "INSTANT",
          email: email ? email : undefined,
          latitude: String(latitude),
          longitude: String(longitude),
          timezone: String(timezone),
          amount: String(amount),
        })
      ),
      amount: String(calculateTotalBreakdown(values)),
      payWith: "CASH",
    };

    try {
      const authResponse = await axios.post(
        `${Baseurl}/api/admin/orders`,
        formData,
        {...config}
      );
      setSuccess(true);
      toast.success(authResponse?.data.data.message);
      setStep(4);
      setTimeout(() => {
        navigate(`/orders/receipt/${authResponse?.data.data.id}`);
      }, 500);
    } catch (error: any) {
      toast.error(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="!grow !flex !h-full !gap-4 !overflow-y-hidden !px-6 !py-2">
      <Formik
        initialValues={initialValues}
        enableReinitialize={false}
        validationSchema={validationSchema}
        validateOnChange={true}
        onSubmit={onSubmit}
      >
        {({
          setFieldValue,
          setFieldTouched,
          errors,
          touched,
          values,
          handleChange,
          handleBlur,
          submitForm,
          setValues,
          handleSubmit,
        }) => {
          return (
            <Form
              action=""
              autoComplete="off"
              className="!grow !flex !gap-4 !overflow-y-hidden"
            >
              {step < 5 && (
                <div className="!grow !overflow-y-auto !scrollbar-hide">
                  <div className="flex gap-3">
                    <GoBackButton />
                  </div>
                  <h2 className="!text-[28px] !font-bold !text-gray-700 !mb-2">
                    Create Order
                  </h2>
                  <p className="!text-sm !text-gray-400 !mb-8">
                    Provide details of your delivery and order below
                  </p>
                  {/* Service Type */}
                  <ServiceType
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                    setStep={setStep}
                    step={step}
                  />
                  {/* Pickup Details */}
                  <PickupDetails
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    touched={touched}
                    values={values}
                    setStep={setStep}
                    step={step}
                    initialValues={initialValues}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                  />
                  <DeliveryDetails
                    ref={deliveryDetailsRef}
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    touched={touched}
                    values={values}
                    setStep={setStep}
                    step={step}
                    getEstimate={() => getEstimate(values, setValues)}
                    initialValues={initialValues}
                    setFieldValue={setFieldValue}
                    isLoading={isLoading}
                    setFieldTouched={setFieldTouched}
                    isCheckoutLoading={isCheckoutLoading}
                  />
                </div>
              )}
              <div
                className={`${
                  step > 4 ? "!grow" : "!min-w-[371px] !max-w-[371px]"
                } !h-full !overflow-hidden`}
              >
                <div className="!h-full !w-full !border !border-gray-100 !bg-gray-50 !overflow-hidden !scrollbar-hide !rounded-[20px] !py-6 !px-4 !flex !flex-col">
                  <p className="!font-semibold !text-gray-400 !mb-4">
                    Estimate Summary
                  </p>
                  {step > 2 ? (
                    <>
                      <div className="!grow !overflow-y-scroll !scrollbar-hide">
                        <div className="!bg-white !rounded-[10px] !py-6 !px-5 !space-y-4 !mb-3">
                          <p className="!text-lg !font-semibold !text-gray-700">
                            Pickup Details
                          </p>
                          <p className="!font-medium !text-gray-500 !text-sm">{`${values.first_name} ${values.last_name}`}</p>
                          <p className="!font-medium !text-gray-500 !text-sm">
                            {values.address}
                          </p>
                          <p className="!font-medium !text-gray-500 !text-sm">
                            Service Type -{" "}
                            <span className="!text-gray-700">
                              {values.accountType === "WITHIN_LAGOS"
                                ? "Ship Within Lagos"
                                : values.accountType === "OUTSIDE_LAGOS"
                                ? "Ship Outside Lagos"
                                : "Ship Outside Nigeria"}
                            </span>
                          </p>
                        </div>
                        <div className="!bg-white !rounded-[10px]">
                          <div className="!pt-6 !px-5 !space-y-4 !mb-3">
                            {values.deliveries.map(
                              (
                                {
                                  d_address,
                                  d_city,
                                  d_description,
                                  d_phone_number,
                                  d_schedule,
                                  d_state,
                                  d_weight,
                                  full_name,
                                  amount,
                                },
                                index
                              ) => {
                                return (
                                  <div className="!space-y-4" key={index}>
                                    <div className="!flex !items-center !justify-between !pb-2">
                                      <p className="!text-base !font-semibold !text-gray-700">
                                        Delivery {index + 1}
                                      </p>

                                      <button
                                        onClick={() => {
                                          setStep(2);
                                          setTimeout(() => {
                                            deliveryDetailsRef.current?.scrollToDelivery(
                                              index
                                            );
                                          }, 100);
                                        }}
                                      >
                                        <svg
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M3 21L12 21H21"
                                            stroke="#36394A"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                          />
                                          <path
                                            d="M12.2218 5.8279L15.0503 2.99947L20 7.94922L17.1716 10.7776M12.2218 5.8279L6.61522 11.4345C6.42769 11.622 6.32233 11.8764 6.32233 12.1416L6.32233 16.6771L10.8579 16.6771C11.1231 16.6771 11.3774 16.5718 11.565 16.3842L17.1716 10.7776M12.2218 5.8279L17.1716 10.7776"
                                            stroke="#36394A"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                    <p className="!font-medium !text-gray-500 !text-sm">
                                      {full_name}
                                    </p>
                                    <p className="!font-medium !text-gray-500 !text-sm">
                                      {d_address}
                                    </p>
                                    <p className="!font-medium !text-gray-500 !text-sm">
                                      {d_phone_number}
                                    </p>

                                    <p className="!font-medium !text-gray-700 !text-sm">
                                      Product Details
                                    </p>
                                    <p className="!font-medium !text-gray-500 !text-sm">
                                      Weight -{" "}
                                      <span className="!text-gray-700">
                                        {d_weight}kg
                                      </span>
                                    </p>
                                    <p className="!font-medium !text-gray-500 !text-sm">
                                      {d_description}
                                    </p>

                                    <p className="!font-medium !text-gray-500 !text-sm">
                                      {d_schedule === "Same Day"
                                        ? "Same Day"
                                        : d_schedule === "Next Day"
                                        ? "Next Day"
                                        : d_schedule === "3 - 5 Business Days"
                                        ? "3 - 5 Business days"
                                        : d_schedule === "5 - 7 Business Days"
                                        ? "5 - 7 Business days"
                                        : "7 - 10 Business days"}
                                    </p>
                                    <div className="!pt-2 !border-b !border-b-gray-100 ">
                                      <p className="!font-medium !text-gray-500 !text-xs !mb-2">
                                        COST
                                      </p>
                                    </div>
                                    <p className="!text-2xl !font-semibold !text-gray-700">
                                      ₦{amount.toLocaleString()}
                                    </p>
                                  </div>
                                );
                              }
                            )}
                          </div>

                          <div className="!py-6 !px-5">
                            {coupon ? (
                              <>
                                <div className="!flex !flex-col !gap-1">
                                  <label
                                    htmlFor={""}
                                    className="!text-sm !text-gray-600"
                                  >
                                    Coupon Code
                                  </label>
                                  <input
                                    name={""}
                                    id={""}
                                    placeholder={"Enter coupon code"}
                                    className={`!w-full !rounded-lg !border !bg-white !px-4 !py-3 !text-sm !text-gray-900			focus:!outline-none !border-gray-100 !mb-4
			`}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setCoupon(false)}
                                >
                                  Reedem Code
                                </button>
                              </>
                            ) : (
                              <>
                                {/* {step < 4 && (
                                <p className="font-medium text-[#282828] mb-6">
                                  Have a coupon code from us?{" "}
                                  <button
                                    className="text-[#fe7f1f]"
                                    onClick={() => setCoupon(true)}
                                  >
                                    Click here
                                  </button>
                                </p>
                              )} */}
                                <div className="">
                                  <h3 className="text-base font-semibold mb-4 text-gray-800">
                                    Order Summary
                                  </h3>

                                  <div className="space-y-3">
                                    {/* Subtotal */}
                                    <div className="flex justify-between items-center text-gray-700">
                                      <span className="text-sm">Subtotal</span>
                                      <span className="font-medium text-base">
                                        N
                                        {calculateTotalBreakdown(
                                          values
                                        ).toLocaleString()}
                                      </span>
                                    </div>

                                    {/* Separator */}
                                    <hr className="border-gray-200" />

                                    {/* Total */}
                                    <div className="flex justify-between items-center text-gray-900 font-bold mb-4">
                                      <span className="text-lg">Total</span>
                                      <span className="text-lg">
                                        N
                                        {calculateTotalBreakdown(values)
                                          .toFixed(2)
                                          .toLocaleString()}
                                      </span>
                                    </div>
                                  </div>

                                  <p className="mb-6 text-xs text-greyscale-500">
                                    By clicking on the Create Order button, you
                                    agree to our{" "}
                                    <Link
                                      to={"#"}
                                      className="font-medium text-primary-300"
                                    >
                                      Terms and Conditions
                                    </Link>
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      {step < 5 && (
                        <div className="!flex !justify-between !w-full !mt-6">
                          <button
                            type="submit"
                            onClick={() => onSubmit(values)}
                            className="!bg-[#fe7f1f] disabled:!bg-[#fe7f1f]  !rounded-lg !text-white !text-sm !py-2 !px-4"
                            disabled={
                              calculateTotalBreakdown(values) < 1 ||
                              values.deliveries.some(
                                (item: any) => Number(item.amount) === 0
                              ) ||
                              isLoading
                            }

                            // onClick={() => {
                            //   checkout(values);
                            // }}
                          >
                            {isLoading ? (
                              <div className={`!animate-spin px-4 !py-0`}>
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M21.5998 9.12358C22.6465 8.80994 23.767 9.40515 23.9045 10.4892C24.1676 12.5624 23.886 14.6808 23.0709 16.6299C21.9997 19.1912 20.0736 21.3019 17.6207 22.6022C15.1678 23.9026 12.3399 24.3123 9.61888 23.7614C6.89782 23.2105 4.45195 21.7332 2.69802 19.5811C0.944092 17.429 -0.00937837 14.7354 6.94861e-05 11.9592C0.00951734 9.18292 0.981299 6.49583 2.74983 4.35576C4.51837 2.21568 6.97424 0.755031 9.69898 0.222678C11.7725 -0.182439 13.9041 -0.0308927 15.8816 0.645112C16.9156 0.998567 17.2725 2.21606 16.7541 3.17799C16.2358 4.13992 15.0356 4.47218 13.9764 4.20369C12.8328 3.91381 11.6314 3.87703 10.4578 4.10633C8.63152 4.46314 6.98549 5.44213 5.80014 6.8765C4.61479 8.31087 3.96346 10.1119 3.95713 11.9726C3.9508 13.8334 4.58985 15.6388 5.76541 17.0812C6.94097 18.5236 8.5803 19.5138 10.4041 19.883C12.2278 20.2522 14.1232 19.9777 15.7672 19.1061C17.4113 18.2345 18.7022 16.8199 19.4202 15.1032C19.8816 13.9999 20.0879 12.8158 20.0347 11.6373C19.9855 10.5456 20.553 9.43722 21.5998 9.12358Z"
                                    fill="white"
                                  />
                                </svg>
                              </div>
                            ) : (
                              "Create order"
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="!flex !flex-col !items-center !justify-center !bg-white !rounded-[10px] !grow">
                      <svg
                        width="226"
                        height="227"
                        viewBox="0 0 226 227"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="44.7407"
                          cy="113.853"
                          r="5.93942"
                          fill="#F1F1F1"
                          stroke="black"
                          stroke-width="0.438575"
                        />
                        <rect
                          x="41.1484"
                          y="83.0586"
                          width="108.804"
                          height="12.3174"
                          rx="5.2629"
                          fill="#F1F1F1"
                        />
                        <rect
                          x="78.6133"
                          y="107.694"
                          width="108.804"
                          height="12.3174"
                          rx="5.2629"
                          fill="#F1F1F1"
                        />
                        <rect
                          x="58.085"
                          y="132.841"
                          width="74.9309"
                          height="12.3174"
                          rx="5.2629"
                          fill="#F1F1F1"
                        />
                        <rect
                          x="41.9372"
                          y="94.4245"
                          width="65.4036"
                          height="42.8217"
                          rx="2.85074"
                          transform="rotate(-12.9733 41.9372 94.4245)"
                          fill="#F3F3F3"
                          stroke="#999999"
                          stroke-width="1.31573"
                        />
                        <rect
                          x="76.7047"
                          y="104.246"
                          width="65.4036"
                          height="42.8217"
                          rx="2.85074"
                          fill="#F3F3F3"
                          stroke="#999999"
                          stroke-width="1.31573"
                        />
                        <rect
                          x="77.5869"
                          y="115.392"
                          width="63.64"
                          height="9.23806"
                          fill="#DDDDDD"
                        />
                        <path
                          d="M77.5869 115.392H101.709M104.018 115.392H109.92M112.23 115.392H135.838M140.97 124.63H124.034M118.132 124.63H88.8779"
                          stroke="#999999"
                          stroke-width="1.31573"
                          stroke-linecap="round"
                        />
                        <circle
                          cx="156.111"
                          cy="139"
                          r="5.93942"
                          fill="#F1F1F1"
                          stroke="black"
                          stroke-width="0.438575"
                        />
                        <circle
                          cx="168.428"
                          cy="89.2173"
                          r="6.15871"
                          fill="#F1F1F1"
                        />
                      </svg>

                      <p className="!font-medium !text-gray-300 !text-lg !mt-4">
                        No Estimate Yet
                      </p>
                    </div>
                  )}
                </div>
              </div>
              {step > 3 && (
                <PaymentDrawer
                  isLoading={isLoading}
                  success={success}
                  setSuccess={setSuccess}
                  submitForm={submitForm}
                  setStep={setStep}
                />
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
